import { SyncLoader } from "react-spinners";
import { useGetRecapQuery } from "../../slices/recapApiSlice";
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dataApplicationsContext from "../../context/dataApplicationsContext";
import { useGetAllUsersQuery } from "../../slices/usersApiSlice";
import { getRemainingGroups } from "../../utils/remaining";

const Recap = () => {
  const { setOpenModalNewUser, setEdittingUser, setIsComingFromRecap, selectedSeason } = useContext(dataApplicationsContext);
  const { data: recap, isLoading: isLoadingRecap, isSuccess: isSuccessRecap } = useGetRecapQuery(selectedSeason?.value, { skip: !selectedSeason }); 
  const { data: users, isLoading: isLoadingUser, isSuccess: isSuccessUser, refetch } = useGetAllUsersQuery(selectedSeason?.value, { skip: !selectedSeason }); 
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerms, setSearchTerms] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});

  const groupsToRefund = useMemo(
    () => getRemainingGroups(users).filter((g) => g.remaining < 0),
    [users]
  );
  const totalToRefund = groupsToRefund.reduce((sum, g) => sum + Math.abs(g.remaining), 0);
  const nbToRefund = groupsToRefund.reduce((sum, g) => sum + g.members.length, 0);
  
  const navigate = useNavigate();

  if (isLoadingRecap) {
    return (
      <div className="main-loader-container">
        <div className="main-loader">
          <img src="/images/logo_cgv.png" alt="Error-404" className="logo-loader"></img>
          <SyncLoader color='#e1004a' size={16}/>
        </div>
      </div>
    )
  }

  // Vérification de sécurité
  if (!recap || !recap.detail) {
    return <div>Aucune donnée disponible</div>;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(parseFloat(amount));
  };

  const getPercentage = (amount) => {
    const percentage = (parseFloat(amount) / parseFloat(recap.total)) * 100;
    return percentage.toFixed(1);
  };

  const getMethodColor = (index) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    return colors[index % colors.length];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const toggleRow = (methodId) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(methodId)) {
      newExpandedRows.delete(methodId);
    } else {
      newExpandedRows.add(methodId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSearchChange = (methodId, searchValue) => {
    setSearchTerms(prev => ({
      ...prev,
      [methodId]: searchValue
    }));
  };

  const toggleMonth = (methodId, monthYear) => {
    const key = `${methodId}-${monthYear}`;
    setExpandedMonths(prev => {
      const currentState = prev[key] === undefined ? true : prev[key];
      return {
        ...prev,
        [key]: !currentState
      };
    });
  };


  const isMonthExpanded = (methodId, monthYear) => {
    const key = `${methodId}-${monthYear}`;
    return expandedMonths[key] === undefined ? true : expandedMonths[key]; // Par défaut ouvert
  };

  const totalTransactions = recap.detail.reduce((sum, method) => 
    sum + parseInt(method.total_payments), 0
  );

  // Créer une copie triée du tableau pour éviter l'erreur de mutation
  const sortedMethods = [...recap.detail].sort((a, b) => 
    parseFloat(b.total_amount) - parseFloat(a.total_amount)
  );

  const handleEditUser = (id_user) => {
    const clicUser = users.find(user => user.id_user === id_user)
    setIsComingFromRecap(true);
    setEdittingUser(clicUser);
    setOpenModalNewUser(true);
    navigate('/effectifs')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Tableau de Bord des Paiements
          </h1>
          <p className="text-gray-600">Analyse détaillée des méthodes de paiement</p>
        </div>

        {/* Cartes de résumé */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total des revenus (brut)</p>
                <p className="text-3xl font-bold text-gray-800">
                  {formatCurrency(recap.total)}
                </p>
                <p className="text-xs font-semibold text-gray-500 mt-1">
                  Net après remb. : {formatCurrency(
                    parseFloat(recap.total) - parseFloat(recap.refunds?.total_refunds || 0)
                  )}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Reste à rembourser</p>
                <p className="text-3xl font-bold text-gray-800">
                  {formatCurrency(totalToRefund)}
                </p>
                <p className="text-xs font-semibold text-gray-500 mt-1">
                  {nbToRefund} adhérent(s) concerné(s)
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total transactions</p>
                <p className="text-3xl font-bold text-gray-800">
                  {totalTransactions.toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Trop perçu total</p>
                <p className="text-3xl font-bold text-gray-800">
                  {formatCurrency(recap.overpayments?.total_overpayments || 0)}
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-200 border-b border-gray-300">
            <h2 className="text-2xl font-bold text-gray-800">Détail des méthodes de paiement</h2>
            <p className="text-sm text-gray-600 mt-1">Cliquez sur une ligne pour voir les détails des paiements</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Méthode
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant moyen
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Part du total
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedMethods.map((method, index) => {
                  const totalAmount = method.total_amount;
                  const totalCount = method.total_payments;
                  const avgAmount = parseFloat(totalAmount) / parseInt(totalCount);
                  const isExpanded = expandedRows.has(method.id_payment_method);
                  
                  return (
                    <React.Fragment key={method.id_payment_method}>
                      <tr 
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleRow(method.id_payment_method)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full ${getMethodColor(index)} mr-3`}></div>
                            <span className="font-medium text-gray-900">{method.lib_payment_method}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">
                          {formatCurrency(totalAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                          {totalCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                          {formatCurrency(avgAmount.toString())}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getPercentage(totalAmount)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                        </td>
                      </tr>
                      
                      {/* Ligne accordéon pour les détails */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="6" className="px-0 py-0">
                            <div className="border-b border-gray-300 border-t border-gray-300">
                              <div className="px-6 py-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="text-sm font-medium text-gray-900">
                                    Détail des paiements - {method.lib_payment_method}
                                  </h4>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      placeholder="Rechercher un utilisateur..."
                                      value={searchTerms[method.id_payment_method] || ''}
                                      onChange={(e) => handleSearchChange(method.id_payment_method, e.target.value)}
                                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                                    />
                                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  </div>
                                </div>
                                <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden pr-6">
                                  {(() => {
                                    const payments = method.payment_list || method.details || [];
                                    const searchTerm = searchTerms[method.id_payment_method] || '';
                                    
                                    // Filtrer les paiements selon le terme de recherche
                                    const filteredPayments = payments.filter(payment => {
                                      if (!searchTerm) return true;
                                      const fullName = `${payment.first_name} ${payment.last_name}`.toLowerCase();
                                      return fullName.includes(searchTerm.toLowerCase());
                                    });
                                    
                                    // Grouper les paiements filtrés par mois
                                    const groupedByMonth = filteredPayments.reduce((groups, payment) => {
                                      const date = new Date(payment.versement_date);
                                      const monthYear = date.toLocaleDateString('fr-FR', { 
                                        year: 'numeric', 
                                        month: 'long' 
                                      });
                                      
                                      if (!groups[monthYear]) {
                                        groups[monthYear] = [];
                                      }
                                      groups[monthYear].push(payment);
                                      return groups;
                                    }, {});
                                    
                                    // Trier les mois par ordre chronologique ascendant (plus ancien au plus récent)
                                    const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => {
                                      const dateA = new Date(groupedByMonth[a][0].versement_date);
                                      const dateB = new Date(groupedByMonth[b][0].versement_date);
                                      return dateA - dateB;
                                    });
                                    
                                    if (filteredPayments.length === 0 && searchTerm) {
                                      return (
                                        <div className="text-center py-8">
                                          <div className="text-gray-400 mb-2">
                                            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                          </div>
                                          <p className="text-gray-500 text-sm">Aucun résultat trouvé pour "{searchTerm}"</p>
                                        </div>
                                      );
                                    }
                                    
                                    return sortedMonths.map(monthYear => {
                                      const monthExpanded = isMonthExpanded(method.id_payment_method, monthYear);
                                      const monthPayments = groupedByMonth[monthYear];
                                      const monthTotal = monthPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
                                      
                                      return (
                                        <div key={monthYear} className="mb-6 last:mb-0">
                                          <div 
                                            className="flex items-center justify-between cursor-pointer hover:bg-gray-100 rounded-lg p-2 -m-2 transition-colors"
                                            onClick={() => toggleMonth(method.id_payment_method, monthYear)}
                                          >
                                            <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                              {monthYear}
                                            </h5>
                                            <div className="flex items-center space-x-3">
                                              <span className="text-sm font-semibold text-gray-600">
                                                {formatCurrency(monthTotal)}
                                              </span>
                                              <svg 
                                                className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                                                  monthExpanded ? 'rotate-180' : ''
                                                }`} 
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                              >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                              </svg>
                                            </div>
                                          </div>
                                          
                                          <div className="border-b border-gray-200 mb-3 mt-1"></div>
                                          
                                          {monthExpanded && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                              {monthPayments.map((payment, paymentIndex) => (
                                                <div 
                                                  key={`${payment.id_user}-${paymentIndex}`}
                                                  onClick={() => handleEditUser(payment.id_user)}
                                                  className="flex items-center justify-between py-3 px-4 bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
                                                >
                                                  <div className="flex-1">
                                                    <div className="flex items-center space-x-3">
                                                      <div className="flex-shrink-0">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                                          <span className="text-sm font-semibold text-white">
                                                            {payment.first_name?.[0]}{payment.last_name?.[0]}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                          {payment.first_name} {payment.last_name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                          {formatDate(payment.versement_date)}
                                                        </p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <div className="text-right ml-3">
                                                    <p className="text-lg font-bold text-gray-900">
                                                      {formatCurrency(payment.amount)}
                                                    </p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8 items-start">
          {recap.overpayments?.overpayment_list?.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-8">
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                <div className="px-6 py-4 bg-gray-200 border-b border-gray-300">
                  <h2 className="text-2xl font-bold text-gray-800">Trop perçu & régulation de solde</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Total : {formatCurrency(recap.overpayments.total_overpayments)}
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {recap.overpayments.overpayment_list.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleEditUser(item.id_user)}
                      className="flex items-center justify-between px-6 py-4 hover:bg-gray-200 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-white">
                            {item.first_name?.[0]}{item.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {item.first_name} {item.last_name}
                          </p>
                          {item.motif && (
                            <p className="text-xs text-gray-500">{item.motif}</p>
                          )}
                          <p className="text-xs text-gray-400">
                            {formatDate(item.created_at)}
                          </p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-red-500">
                        +{formatCurrency(item.amount_overpayment)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {groupsToRefund.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-8">
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                <div className="px-6 py-4 bg-gray-200 border-b border-gray-300">
                  <h2 className="text-2xl font-bold text-gray-800">Remboursements à effectuer</h2>
                  <p className="text-sm text-gray-600 mt-1">Total : {formatCurrency(totalToRefund)}</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {[...groupsToRefund]
                    .sort((a, b) => a.remaining - b.remaining)
                    .map((g) => (
                      <div
                        key={g.key}
                        onClick={() => handleEditUser(g.members[0].id_user)}
                        className="flex items-center justify-between px-6 py-4 hover:bg-gray-200 cursor-pointer transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          {g.members.map((m) => `${m.first_name} ${m.last_name}`).join(' / ')}
                        </p>
                        <p className="text-lg font-bold text-purple-600">
                          {formatCurrency(Math.abs(g.remaining))}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Recap;